import { useState } from "react";

import { PERMISSION_KEY, TRANSLATE_PREFIX } from "../..";
import {
  getAllContractSweepRecipient,
  getExportContractSweepRecipient,
} from "../../../../../api/merchant";
import { Ipage } from "../../../../../api/types";
import GeneralBtn from "../../../../../components/GeneralBtn";
import { Box, Container } from "../../../../../components/MuiGenerals";
import TableTab from "../../../../../components/TableTab";
import { useAlerting, usePermission } from "../../../../../hooks";
import {
  displayAmount,
  downloadFiles,
  findChainInfo,
  findDecimalByChainNameAndAsset,
  getFullApiResponse,
  listMappingTransform,
  toDisplayTime,
} from "../../../../../utils/helper";
import { DivideLine } from "../../../../../utils/HelperComp";
import { customSx } from "../../../../../utils/styling";
import { createZusInstance } from "../../../../../zustand/store";
import Table from "./components/Table";
import { Filter } from "./components/Filter";
import cleanParams from "../../helpers/cleanParams";
import { EXPORT_FILE_NAME, TAB_TRANSLTE_PREFIX } from "./constants";
import { ApiParams } from "./types/ApiParams";
import useTabTranslation from "src/hooks/useTabTranslation";
import { GetAllRes } from "./types/GetAllRes";
import { RiskType } from "@wallet-manager/node-types/dist/src/postgres/const";

export const initZusParams = {
  page: 0,
  pageSize: 20,
} as const;

export const useZusParams = createZusInstance<Ipage & Partial<ApiParams>>(
  initZusParams,
);

export const apis = {
  getAll: getAllContractSweepRecipient,
  export: getExportContractSweepRecipient,
};

function ContractSweepRecipientTab() {
  const zusParams = useZusParams();
  const { hasPermission } = usePermission();
  const { alerting } = useAlerting();
  const { t, tc, translate } = useTabTranslation({
    featurePrefix: TRANSLATE_PREFIX,
    tabPrefix: TAB_TRANSLTE_PREFIX,
  });
  const [count, setCount] = useState(0);
  const onExport = async () => {
    if (count === 0) {
      return alerting("error", tc("no_data_export"));
    }
    const apiFn = (page: number, pageSize: number, signal: any) =>
      apis.export(
        { ...cleanParams(zusParams.body), page, pageSize },
        { signal },
      );
    const rawRes = await getFullApiResponse(apiFn, count, true);
    if (rawRes.length === 0) return;
    const omitKeys = ["rawData"];

    const res = listMapping("name", rawRes, { t, omitKeys, translate });
    const config = {};
    downloadFiles(EXPORT_FILE_NAME, res, config);
  };

  const isExportBtnShown = hasPermission(
    PERMISSION_KEY.TabContractSweepRecipient.Export,
  );

  return (
    <Box
      sx={{
        backgroundColor: "white",
        paddingTop: "2rem",
        paddingLeft: "2rem",
        paddingRight: "2rem",
      }}
    >
      <Container disableGutters maxWidth={false}>
        <Filter />
      </Container>
      <DivideLine />
      <Container
        style={customSx.datagridContainer}
        maxWidth={false}
        disableGutters
      >
        <Box sx={customSx.tableExportBar}>
          {isExportBtnShown && <GeneralBtn label="export" onClick={onExport} />}
        </Box>
        <TableTab>
          <Table {...{ listMapping, setCount }} />
        </TableTab>
      </Container>
    </Box>
  );
}

const listMapping = (
  which: "key" | "name",
  array: GetAllRes[],
  config?: { omitKeys: string[]; t: any; translate: any },
): any[][] => {
  const { omitKeys = [], t, translate } = config || {};
  const res = array.map((item: GetAllRes) => {
    const chainInfo = findChainInfo(String(item.chain_type), item.chain_id);
    const decimals = findDecimalByChainNameAndAsset(
      String(item.chain_type),
      String(item.chain_id),
      String(item.asset_name),
    );

    const getRiskType = (type: RiskType) => {
      if (!RiskType[type]) {
        return "";
      }

      return translate(`enumRiskType.${RiskType[type]}`);
    };

    const mappedResult = [
      ["chainName", t("chainName"), chainInfo?.name],
      ["currency", t("currency"), item.asset_name],
      [
        "groupName",
        t("groupName"),
        item.auto_sweep_asset_dest_balance_report?.group_name,
      ],
      [
        "walletDisplayName",
        t("walletDisplayName"),
        item.dest_wallet?.display_name,
      ],
      ["recipientAddress", t("recipientAddress"), item.wallet_address],
      ["riskType", t("riskType"), getRiskType(item.risk_type)],
      ["amount", t("amount"), displayAmount(item.balance, decimals)],
      [
        "activeStartTime",
        t("activeStartTime"),
        toDisplayTime(item.auto_sweep_asset_dest_balance_report?.enabled_time),
      ],
      [
        "activeEndTime",
        t("activeEndTime"),
        toDisplayTime(item.auto_sweep_asset_dest_balance_report?.disabled_time),
      ],
    ].filter(([key]) => !omitKeys.includes(key));
    return mappedResult;
  });

  const output = res.map(listMappingTransform(which));
  return output;
};

export default ContractSweepRecipientTab;
